import { useMemo, useRef } from 'react';
import { type InfiniteData, keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { addDays, addHours, differenceInDays, format, parse, startOfDay, subDays, subHours } from 'date-fns';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import type { EpgChannel, EpgProgram } from '@jwp/ott-common/types/epg';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { LIVE_CHANNELS_REFETCH_INTERVAL } from '@jwp/ott-common/src/constants';
import EpgController from '@jwp/ott-common/src/controllers/EpgController';

const useInfiniteLiveChannels = ({
  playlist,
  maxPastDays = 8,
  maxFutureDays = 8,
  fetchMarginHours = 2, // How many extra hours to request for the given date
  dayOffset = 0,
}: {
  playlist: PlaylistItem[];
  maxPastDays?: number;
  maxFutureDays?: number;
  fetchMarginHours?: number;
  dayOffset?: number;
}) => {
  const epgController = getModule(EpgController);

  const today = new Date();
  const todayParam = format(today, 'MM-dd-yyyy');
  const todayWithOffsetParam = format(addDays(today, dayOffset), 'MM-dd-yyyy');

  const {
    data: infiniteData,
    status,
    isFetching,
    isLoading,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
  } = useInfiniteQuery<EpgChannel[], Error, InfiniteData<EpgChannel[]>, string[], string>({
    queryKey: ['epg', ...playlist.map(({ mediaid }) => mediaid), todayWithOffsetParam],
    initialPageParam: todayWithOffsetParam,
    refetchInterval: LIVE_CHANNELS_REFETCH_INTERVAL,
    placeholderData: keepPreviousData,
    getNextPageParam: (_lastPage, _allPages, lastPageParam) => {
      const lastPageDate = parse(lastPageParam, 'MM-dd-yyyy', new Date());
      const todayParamDate = parse(todayParam, 'MM-dd-yyyy', new Date());
      const diff = differenceInDays(lastPageDate, todayParamDate);

      if (diff >= maxFutureDays) return undefined;

      return format(addDays(lastPageDate, 1), 'MM-dd-yyyy');
    },
    getPreviousPageParam: (_lastPage, _allPages, lastPageParam) => {
      const lastPageDate = parse(lastPageParam, 'MM-dd-yyyy', new Date());
      const todayParamDate = parse(todayParam, 'MM-dd-yyyy', new Date());
      const diff = differenceInDays(lastPageDate, todayParamDate);

      if (diff <= -maxPastDays) return undefined;

      return format(subDays(lastPageDate, 1), 'MM-dd-yyyy');
    },
    queryFn: ({ pageParam }) => {
      const pageParamDate = parse(pageParam, 'MM-dd-yyyy', new Date());
      const startTime = subHours(startOfDay(pageParamDate), fetchMarginHours).getTime();
      const endTime = addHours(startOfDay(addDays(pageParamDate, 1)), fetchMarginHours).getTime();

      return epgController.getSchedules(playlist, startTime, endTime);
    },
  });

  // Combine all infinite query pages into a list of channels with all programs
  const channels = useMemo<EpgChannel[]>(() => {
    if (!infiniteData) return [];

    const channelMap = new Map<string, EpgChannel>();

    for (const page of infiniteData.pages) {
      for (const channel of page) {
        const existing = channelMap.get(channel.id);

        if (!existing) {
          channelMap.set(channel.id, {
            ...channel,
            programs: [...channel.programs],
          });
        } else {
          // Merge programs without duplicates
          const programMap = new Map<string, EpgProgram>(existing.programs.map((program) => [program.id, program]));

          for (const program of channel.programs) {
            programMap.set(program.id, program);
          }

          existing.programs = Array.from(programMap.values()).sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
        }
      }
    }

    return Array.from(channelMap.values());
  }, [infiniteData]);

  // Cache "today" channels for Live Now view, only update when dayOffset is 0
  const channelsTodayRef = useRef<EpgChannel[]>(channels);
  if (dayOffset === 0 && channels.length > 0) {
    channelsTodayRef.current = channels;
  }

  return {
    status,
    channels,
    channelsToday: channelsTodayRef.current,
    isFetching,
    isLoading,
    hasNextPage,
    hasPreviousPage,
    loadedDays: (infiniteData?.pageParams || []) as string[],
    fetchNextPage,
    fetchPreviousPage,
  };
};

export default useInfiniteLiveChannels;
